import type { AddressShipmentOptional } from "../../types";

/**
 * How the container-orders read views turn stored values into readable ones.
 *
 * Kept apart from `readonlyFields.tsx` — which holds the components — only
 * because a module that exports both components and plain functions breaks fast
 * refresh. Same job, same pair of readers: the order's read view and the
 * container's.
 */

/** A dash, so an empty value still occupies its slot and the grid stays aligned. */
export const EMPTY = "—";

/** Country arrives populated on a loaded record but is an id on a fresh one. */
const countryName = (country: unknown): string => {
  if (!country) return "";
  if (typeof country === "string") return "";
  return (country as { name?: string }).name ?? "";
};

/** One readable line: street, postcode city, state, country. */
export function formatAddress(address?: AddressShipmentOptional | null) {
  if (!address) return undefined;
  const cityLine = [address.postalCode, address.city].filter(Boolean).join(" ");
  const parts = [
    address.street,
    cityLine,
    address.state,
    countryName(address.country),
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : undefined;
}

/** `toLocaleDateString`, or undefined so `Field` shows its dash. */
export const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : undefined;

/**
 * The same, with the time.
 *
 * For the handful of values that are moments rather than dates — when the
 * office released a container, when a step was stamped. A booked slot is a day
 * and reads as one; a thing that happened happened at a time, and printing only
 * its date throws away the half of it anyone asking would want.
 */
export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "short",
        timeStyle: "short",
      })
    : undefined;
